import { Root, Item } from '@radix-ui/react-toggle-group';
import { vars } from '../../theme';
import React, { ReactElement, ElementType, ReactNode } from 'react';
import styled from 'styled-components';
import { transition } from '../../styles/animations';
import { MustIncludeOneOfBasicTestIdProps } from '../common/internal/test-id.types';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';
import HBText from '../HBText/HBText';
import { Check16 } from '@honeybook/icons';

export interface IHBButtonListItem<T extends string> {
    value: T;
    title?: string;
    description?: string;
    icon?: ReactNode;
    disabled?: boolean;
    /** Highlights the item with a red border to indicate a validation error. */
    error?: boolean;
    testIdName?: string;
}

export interface HBButtonListItemProps<T extends string>
    extends IHBButtonListItem<T> {
    /** Custom render function that replaces the default item layout (icon + title/description + badge). */
    render?: (props: Omit<HBButtonListItemProps<T>, 'render'>) => ReactElement;
    /** Custom element rendered in the top-right corner of the item, replacing the default check icon when selected. */
    badge?: JSX.Element;
    /** When true, shows a blue check icon on the selected item; overridden if `badge` is provided. */
    showSelectedIcon?: boolean;
}

type THBButtonListItemBadge<T extends string> = Pick<
    HBButtonListItemProps<T>,
    'badge' | 'showSelectedIcon'
>;

function HBButtonListItemBadge<T extends string>({
    badge,
    showSelectedIcon
}: THBButtonListItemBadge<T>) {
    if (badge) {
        return badge;
    } else if (showSelectedIcon) {
        return <StyledCheck16 color={vars.colors.text.accent} />;
    }

    return null;
}

function HBButtonListItem<T extends string>({
    render,
    badge,
    ...props
}: HBButtonListItemProps<T>) {
    if (render) {
        return <>{render(props)}</>;
    }

    return (
        <ItemContentContainer>
            {props.icon && <IconContainer>{props.icon}</IconContainer>}
            <TextContainer>
                {props.title && (
                    <HBText type="emphasis16" color="text.primary">
                        {props.title}
                    </HBText>
                )}
                {props.description && (
                    <HBText type="body14" color="text.secondary">
                        {props.description}
                    </HBText>
                )}
            </TextContainer>
            <HBButtonListItemBadge
                badge={badge}
                showSelectedIcon={props.showSelectedIcon}
            />
        </ItemContentContainer>
    );
}

export interface HBButtonListProps<T extends string> {
    /** Array of selectable button items to render; only one can be selected at a time (single toggle group). */
    buttons: HBButtonListItemProps<T>[];
    /** The currently selected item's value. Pass `undefined` for no initial selection. */
    value: T | undefined;
    /** When true, shows a blue check icon on the selected item; can be overridden per-item via `badge`. */
    showSelectedIcon?: boolean;
    /** Applies red border error styling to all items; individual items can override via their own `error` prop. */
    error?: boolean;
    /** Called when the user selects a button, receiving the newly selected item's `value`. */
    onChange: (value: T) => void;
    /** Global render function applied to all items that don't define their own `render` prop. */
    render?: (props: Omit<HBButtonListItemProps<T>, 'render'>) => ReactElement;
    /** Custom wrapper element that replaces the default vertical/horizontal layout container. */
    wrapper?: ElementType;
    /** Layout direction of the button list. Defaults to `'vertical'`. */
    orientation?: 'horizontal' | 'vertical';
}

export interface HBButtonListWrapperProps<T extends string> {
    button: HBButtonListItemProps<T>;
    showSelectedIcon?: boolean;
    error?: boolean;
    render?: (props: Omit<HBButtonListItemProps<T>, 'render'>) => ReactElement;
}

function HBButtonListItemWrapper<T extends string>({
    button,
    render,
    showSelectedIcon,
    error
}: HBButtonListWrapperProps<T>) {
    const finalTestId = useBuildTestIdInternal({
        testId: null,
        identifier: button.testIdName,
        suffix: 'button-list-item',
        componentName: 'HBButtonList',
        enforce: false
    });
    return (
        <StyledItem
            key={button.value}
            value={button.value}
            disabled={button.disabled}
            data-testid={button.testIdName && finalTestId}
            data-error={(button.error ?? error) || undefined}
        >
            <HBButtonListItem
                {...button}
                render={button.render || render}
                showSelectedIcon={showSelectedIcon}
                error={button.error ?? error}
            />
        </StyledItem>
    );
}

export default function HBButtonList<T extends string>({
    testId,
    testIdName,
    buttons,
    value,
    render,
    onChange,
    wrapper,
    orientation = 'vertical',
    showSelectedIcon = true,
    error = false
}: HBButtonListProps<T> & MustIncludeOneOfBasicTestIdProps) {
    const finalTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName,
        suffix: 'button-list',
        componentName: 'HBButtonList',
        enforce: false
    });

    function getWrapper() {
        if (wrapper) {
            return wrapper;
        }
        if (orientation === 'vertical') {
            return VerticalRoot;
        }
        return HorizontalRoot;
    }

    const Wrapper = getWrapper();

    return (
        <StyledRoot
            type="single"
            value={value}
            onValueChange={(newValue: T) => {
                // Don't allow unselecting
                if (newValue) {
                    onChange(newValue);
                }
            }}
            data-testid={finalTestId}
            asChild={true}
            orientation={wrapper ? undefined : orientation}
        >
            <Wrapper>
                {buttons.map(button => (
                    <HBButtonListItemWrapper
                        key={button.value}
                        button={button}
                        render={render}
                        showSelectedIcon={showSelectedIcon}
                        error={error}
                    />
                ))}
            </Wrapper>
        </StyledRoot>
    );
}

// -------------------------------- Styles -------------------------------- //
const StyledRoot = styled(Root)`
    width: 100%;
`;

const VerticalRoot = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const HorizontalRoot = styled.div`
    display: flex;
    gap: 16px;
`;

const ItemContentContainer = styled.div`
    display: flex;
    gap: 8px;
    padding: 16px;
    position: relative;
`;

const IconContainer = styled.div``;
const TextContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const StyledCheck16 = styled(Check16)`
    position: absolute;
    top: 16px;
    right: 16px;
`;

const StyledItem = styled(Item)`
    /* override angular styles */
    margin: unset;
    text-align: unset;
    padding: unset;
    font-size: unset;
    color: unset;
    &:focus {
        background-color: unset;
        color: unset;
    }
    &:hover {
        color: unset;
    }
    /* override angular end */

    width: 100%;
    background-color: unset;
    border: 1px solid ${vars.colors.stroke.secondary};
    border-radius: ${vars.radius.xs};
    ${transition('all')};

    &:hover,
    &:focus-visible {
        background-color: ${vars.colors.surface.itemHover};
        border-color: ${vars.colors.stroke.primary};
    }

    &[data-state='off'] {
        ${StyledCheck16} {
            display: none;
        }
    }

    &[data-state='on'] {
        background-color: ${vars.colors.surface.accent};
        border-color: ${vars.colors.stroke.accent};

        ${IconContainer} svg * {
            fill: ${vars.colors.text.accent};
        }
    }

    &[data-disabled] {
        opacity: 0.6;
        &:hover {
            background-color: unset;
            border: 1px solid ${vars.colors.stroke.secondary};
            cursor: not-allowed;
        }
    }

    /* error state */
    &[data-error='true'] {
        border-color: ${vars.colors.stroke.danger};
        &:focus-visible {
            border-color: ${vars.colors.stroke.danger};
            background-color: ${vars.colors.surface.danger};
        }
    }
`;
